import DOMPurify from 'dompurify';
import { MARK_CLASSES, splitHighlights } from '../../utils/splitHighlights';

interface HtmlContentProps {
  html: string | null | undefined;
  className?: string;
  /** Search term whose occurrences get wrapped in `<mark>` inside the rendered text. */
  highlightTerm?: string;
}

const ALLOWED_TAGS = ['p', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 's', 'h2', 'h3', 'a', 'br'];

function isHtml(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

function plainTextFallback(text: string): string {
  return text
    .split(/\r?\n/)
    .filter((l) => l.trim())
    .map((l) => `<p>${l}</p>`)
    .join('');
}

/**
 * Wraps every match of the term inside the text nodes of an already sanitised
 * tree in `<mark>`. Runs after DOMPurify and only ever inserts text nodes and
 * bare `<mark>` elements, so nothing from the term or the content can become
 * markup. Matching per text node means a word split across inline tags
 * (`Cur<b>cumin</b>`) is not marked — same limitation as the list view.
 */
function markMatches(root: ParentNode, term: string): void {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text);
  }

  for (const textNode of textNodes) {
    const segments = splitHighlights(textNode.data, term);
    if (!segments.some((segment) => segment.match)) continue;

    const fragment = doc.createDocumentFragment();
    for (const segment of segments) {
      if (segment.match) {
        const mark = doc.createElement('mark');
        mark.className = MARK_CLASSES;
        mark.textContent = segment.text;
        fragment.appendChild(mark);
      } else {
        fragment.appendChild(doc.createTextNode(segment.text));
      }
    }
    textNode.replaceWith(fragment);
  }
}

const proseClasses = [
  '[&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-3 [&_h2]:mb-1',
  '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
  '[&_p]:mb-1',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-0.5 [&_ul]:mb-1',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-0.5 [&_ol]:mb-1',
  '[&_ul_ul]:list-[circle] [&_ul_ul]:pl-5 [&_ul_ul]:mt-0.5',
  '[&_ol_ol]:pl-5 [&_ol_ol]:mt-0.5',
  '[&_a]:text-vivatis-600 [&_a]:underline',
  '[&_strong]:font-bold [&_em]:italic [&_u]:underline [&_s]:line-through',
].join(' ');

export default function HtmlContent({ html, className, highlightTerm }: HtmlContentProps) {
  if (!html) return null;

  const content = isHtml(html) ? html : plainTextFallback(html);
  const sanitized = DOMPurify.sanitize(content, { ALLOWED_TAGS, RETURN_DOM: true }) as HTMLElement;
  const term = highlightTerm?.trim();
  if (term) markMatches(sanitized, term);

  return (
    <div
      className={`${proseClasses} ${className ?? ''}`}
      dangerouslySetInnerHTML={{ __html: sanitized.innerHTML }}
    />
  );
}
